// BUILD-046 Owner direction of 2026-10-21 (cycle A, correction B "consistent overdue"): the ONE rule for "is this task overdue", used by EVERY place the page says Overdue:
// the row label of My Tasks, Shared Tasks and Team / Company (and the legacy Team Work list), the Overdue filter, the Overdue count, the Status-menu Overdue choice and
// the Overdue bucket of Recommended.
//
// Before this rule the page had three: My Tasks counted "not completed and the date is before today" (status-blind, flag-blind), the Shared / Team rows read the server's
// `overdue` flag plus a display rule for unclaimed tasks, and the Overdue CHOICE showed only the Active tab while the Overdue COUNT covered Active, Waiting and Blocked. A
// person could be told "Overdue 3" and then see 1 row. That mismatch is gone because there is now one function and every caller uses it.
//
// ELIGIBLE OVERDUE TASK = all of:
//   1. the task is OPEN: its status is Active, Waiting or Blocked (a Completed task is never overdue, and neither is a task with no / an unknown status);
//   2. its assignment has NOT been cancelled or ended (`lifecycle` 'cancelled', or `outcome` 'removed' / 'cancelled');
//   3. it is NOT shown in its restricted / minimal form (`redacted` true): such a row shows no date, so it can never read Overdue (nothing is revealed);
//   4. EITHER its recorded Task due date is a calendar day BEFORE the current Phoenix day (compared as YYYY-MM-DD strings; a task due today is not overdue until
//      00:00 Phoenix of the next day) OR the server's `overdue` flag is the boolean true. The flag is what the server computed from its own clock when the list was
//      read; the date test uses the page's CURRENT Phoenix day, so a task that became overdue after the list was read is already overdue here, and the page also
//      re-reads the lists when the Phoenix day changes (phoenixDayWatcher.ts) so the server's flags are refreshed.
// Nothing else matters: not `unclaimed` (an unclaimed Shared task past its date is overdue by criterion 4, which is the Owner-approved display rule of 2026-10-20 kept as
// it was), not the follow-up date (it never makes a task overdue), not the priority, not who owns the task.
//
// This changes how rows are written, filtered and counted. It changes no stored data, no permission, no visibility, and not Open_Task_Count or the Shared queue count.
//
// Pure module. Runtime import: the date-only check of dateLabels.ts. Erasable TypeScript only.

import { isIsoDate } from './dateLabels';

/** The fields the rule reads. All optional and untyped so a legacy WorkItem and the WorkTask of the new routes both fit. */
export interface OverdueTask {
  status?: unknown;
  dueDate?: unknown;
  /** The server's flag as of the read. Only the boolean true counts. */
  overdue?: unknown;
  lifecycle?: unknown;
  outcome?: unknown;
  redacted?: unknown;
}

/** True for the three open statuses: Active, Waiting, Blocked. */
export function isOpenStatus(status: unknown): boolean {
  return status === 'active' || status === 'in_progress' || status === 'waiting' || status === 'blocked';
}

/** True when the assignment has been cancelled (`lifecycle`) or has ended (`outcome` removed / cancelled). */
export function assignmentEnded(task: { lifecycle?: unknown; outcome?: unknown }): boolean {
  return task.lifecycle === 'cancelled' || task.outcome === 'removed' || task.outcome === 'cancelled';
}

/** The date test, the only place a Task due date is compared with a day: true when `dueDate` is a calendar day BEFORE `todayIso` (both YYYY-MM-DD). Anything that is not a real date is false. */
export function isOverdueOn(dueDate: unknown, todayIso: unknown): boolean {
  return isIsoDate(dueDate) && isIsoDate(todayIso) && dueDate < todayIso;
}

/** THE rule. `todayIso` is the current Phoenix day (YYYY-MM-DD); anything that is not a real date makes the date test false (the flag may still apply). */
export function isOverdueTask(task: OverdueTask, todayIso: unknown): boolean {
  if (task.redacted === true) return false;
  if (!isOpenStatus(task.status)) return false;
  if (assignmentEnded(task)) return false;
  if (task.overdue === true) return true;
  return isOverdueOn(task.dueDate, todayIso);
}

/** The eligible overdue tasks of `items` (every open status), incoming order kept. A new array. */
export function filterOverdue<T extends OverdueTask>(items: readonly T[], todayIso: unknown): T[] {
  return items.filter((task) => isOverdueTask(task, todayIso));
}

/** How many of `items` are eligible overdue tasks; always the length of `filterOverdue(items, todayIso)`. */
export function countOverdue(items: readonly OverdueTask[], todayIso: unknown): number {
  let count = 0;
  for (const task of items) if (isOverdueTask(task, todayIso)) count += 1;
  return count;
}
